import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ContentPage from '../ContentPage';

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div 
      className="border-b border-gray-200 py-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <button
        className="flex justify-between items-center w-full text-left font-medium focus:outline-none group"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-gray-700 group-hover:text-gray-900 transition-all duration-300">
          {question}
        </span>
        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isOpen ? 'bg-gray-700' : 'bg-gray-400'} transition-all duration-300`}>
          <svg
            className={`w-4 h-4 transition-transform text-white ${isOpen ? 'transform rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pt-4 pb-2 text-gray-600 pl-4 border-l-2 border-gray-200 mt-2">{answer}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQ: React.FC = () => {
  return (
    <ContentPage title="Frequently Asked Questions" accent="purple">
      {/* Subtle decorative elements */}
      <div className="relative mb-8">
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-gray-200 opacity-5 rounded-full blur-xl"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gray-200 opacity-5 rounded-full blur-lg"></div>
        
        <p className="text-lg mb-6 relative z-10 text-gray-700">
          Find answers to common questions about SokoVibe. If you can't find what you're looking for, feel free to
          <span className="text-purple-700 font-medium"> contact us</span>.
        </p>
      </div>
      
      <div className="space-y-4 relative z-10">
        <FAQItem
          question="What is SokoVibe?"
          answer={
            <div>
              <p>
                SokoVibe is an online marketplace specifically designed for Afro-Caribbean businesses in the diaspora. 
                We provide a platform for sellers to showcase their products to customers who value authentic cultural goods.
              </p>
              <div className="mt-2 p-2 bg-gray-50 border-l-2 border-gray-300 rounded-r-md">
                <p className="text-sm italic text-gray-600">"SokoVibe connects cultural heritage with global markets"</p>
              </div>
            </div>
          }
        />
        
        <FAQItem
          question="When will SokoVibe launch?"
          answer={
            <p>
              We're currently in the final stages of development and plan to launch soon. 
              Sign up for our newsletter to be the first to know when we go live!
            </p>
          }
        />
        
        <FAQItem
          question="What types of products can be sold on SokoVibe?"
          answer={
            <p>
              SokoVibe supports a wide range of Afro-Caribbean products including but not limited to:
              <ul className="list-disc pl-5 mt-2">
                <li>Art and crafts</li>
                <li>Fashion and accessories</li>
                <li>Beauty and hair products</li>
                <li>Food and beverages</li>
                <li>Books and literature</li>
                <li>Home décor</li>
                <li>Music and entertainment</li>
              </ul>
            </p>
          }
        />
        
        <FAQItem
          question="How can I sell my products on SokoVibe?"
          answer={
            <p>
              Once we launch, you'll be able to register as a seller, create your store profile, and start listing your products. 
              Our platform is designed to make the process as simple and intuitive as possible. 
              In the meantime, you can join our waiting list to get early access to seller registration.
            </p>
          }
        />
        
        <FAQItem
          question="What countries do you operate in?"
          answer={
            <p>
              SokoVibe will initially focus on connecting Afro-Caribbean sellers with buyers in the UK, Europe, and North America. 
              We plan to expand to more regions as we grow.
            </p>
          }
        />
        
        <FAQItem
          question="How much does it cost to sell on SokoVibe?"
          answer={
            <p>
              We're finalizing our fee structure, which will be competitive and transparent. 
              There will be no upfront costs to create a store, and we'll only take a small commission on successful sales. 
              Premium features and promotional opportunities will be available through our retainership programs.
            </p>
          }
        />
        
        <FAQItem
          question="How will you promote my products?"
          answer={
            <p>
              SokoVibe will promote products through various channels including:
              <ul className="list-disc pl-5 mt-2">
                <li>Our curated marketplace homepage</li>
                <li>Social media campaigns</li>
                <li>Email marketing to targeted customers</li>
                <li>Partnerships with cultural influencers and organizations</li>
                <li>SEO optimization for better visibility</li>
              </ul>
              Sellers on our retainership programs will receive additional promotional benefits.
            </p>
          }
        />
        
        <FAQItem
          question="How do payments work?"
          answer={
            <p>
              SokoVibe will offer secure payment processing with multiple options for customers. 
              Sellers will receive payments directly to their accounts after a brief holding period to ensure customer satisfaction. 
              We'll support major payment methods including credit/debit cards and digital wallets.
            </p>
          }
        />
        
        <FAQItem
          question="What support will I receive as a seller?"
          answer={
            <p>
              All sellers will have access to:
              <ul className="list-disc pl-5 mt-2">
                <li>Seller dashboard with analytics</li>
                <li>Customer service support</li>
                <li>Educational resources and guides</li>
                <li>Community forums to connect with other sellers</li>
              </ul>
              Our retainership programs offer additional premium support services.
            </p>
          }
        />
      </div>
      
      {/* Contact section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        className="mt-12 p-6 bg-gradient-to-r from-yellow-50 via-purple-50 to-teal-50 rounded-xl border border-yellow-100 relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-300 opacity-10 rounded-bl-full"></div>
        <div className="absolute bottom-0 left-0 w-16 h-16 bg-purple-300 opacity-10 rounded-tr-full"></div>
        
        <h3 className="text-lg font-semibold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-teal-600">
          Still have questions?
        </h3>
        <p className="mb-4 text-gray-700 relative z-10">
          Our team is here to help! Reach out to us and we'll get back to you as soon as possible.
        </p>
        <div className="flex flex-wrap gap-4">
          <a 
            href="/contact" 
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-teal-500 text-white rounded-full text-sm font-medium hover:shadow-lg transition-all transform hover:scale-105 duration-200"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Email Support
          </a>
        </div>
      </motion.div>
    </ContentPage>
  );
};

export default FAQ;
